import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import '../../services/GlobalVariables';
import {
    WAITLIST_ANNOUNCEMENT_EVENT,
    WAITLIST_ANNOUNCEMENT_STORAGE_KEY,
    buildWaitlistAnnouncementPayload,
    getTodayQueueDate,
    normalizeQueueDate,
    playWaitlistAnnouncement,
    warmUpWaitlistAnnouncements
} from '../../utils/waitlistAnnouncements';

const ANNOUNCEMENT_ROUTES = new Set(['/DineInTables', '/POS']);
const POLL_INTERVAL_MS = 3000;
const WAITLIST_API_URL = global.SET_API_URL + 'dineInWaitlist/list';

function WaitlistAnnouncementMonitor() {
    const location = useLocation();
    const previousStatusRef = useRef(new Map());
    const announcedWaitlistIdsRef = useRef(new Set());
    const hasInitialSnapshotRef = useRef(false);
    const isFetchingRef = useRef(false);

    const isMonitoringRoute = ANNOUNCEMENT_ROUTES.has(location.pathname);

    useEffect(() => {
        if (typeof window === 'undefined' || !isMonitoringRoute) {
            return undefined;
        }

        const warmUp = () => warmUpWaitlistAnnouncements();

        window.addEventListener('pointerdown', warmUp, { passive: true });
        window.addEventListener('keydown', warmUp);
        window.addEventListener('touchstart', warmUp, { passive: true });

        return () => {
            window.removeEventListener('pointerdown', warmUp);
            window.removeEventListener('keydown', warmUp);
            window.removeEventListener('touchstart', warmUp);
        };
    }, [isMonitoringRoute]);

    useEffect(() => {
        if (typeof window === 'undefined' || !isMonitoringRoute) {
            return undefined;
        }

        const handleAnnouncement = (payload) => {
            if (!payload) {
                return;
            }

            if (payload.waitlistID) {
                announcedWaitlistIdsRef.current.add(String(payload.waitlistID));
            }

            playWaitlistAnnouncement(payload);
        };

        const handleCustomEvent = (event) => {
            handleAnnouncement(event.detail);
        };

        const handleStorageEvent = (event) => {
            if (event.key !== WAITLIST_ANNOUNCEMENT_STORAGE_KEY || !event.newValue) {
                return;
            }

            try {
                handleAnnouncement(JSON.parse(event.newValue));
            } catch (error) {
                // Ignore malformed storage payloads.
            }
        };

        window.addEventListener(WAITLIST_ANNOUNCEMENT_EVENT, handleCustomEvent);
        window.addEventListener('storage', handleStorageEvent);

        return () => {
            window.removeEventListener(WAITLIST_ANNOUNCEMENT_EVENT, handleCustomEvent);
            window.removeEventListener('storage', handleStorageEvent);
        };
    }, [isMonitoringRoute]);

    useEffect(() => {
        if (typeof window === 'undefined' || !isMonitoringRoute) {
            hasInitialSnapshotRef.current = false;
            previousStatusRef.current = new Map();
            announcedWaitlistIdsRef.current = new Set();
            isFetchingRef.current = false;
            return undefined;
        }

        let isActive = true;

        const fetchAndAnnounce = async () => {
            if (isFetchingRef.current) {
                return;
            }

            isFetchingRef.current = true;

            try {
                const response = await axios.get(WAITLIST_API_URL);
                const waitlist = Array.isArray(response.data?.waitlist) ? response.data.waitlist : [];
                const todayDate = getTodayQueueDate();
                const nextStatusMap = new Map();

                waitlist.forEach((entry) => {
                    nextStatusMap.set(String(entry.waitlistID), String(entry.status || ''));
                });

                if (!hasInitialSnapshotRef.current) {
                    previousStatusRef.current = nextStatusMap;
                    hasInitialSnapshotRef.current = true;
                    return;
                }

                const newlyCalledTokens = waitlist
                    .filter((entry) => {
                        const waitlistID = String(entry.waitlistID);
                        const previousStatus = previousStatusRef.current.get(waitlistID);

                        return (
                            normalizeQueueDate(entry.queueDate) === todayDate
                            && String(entry.status || '') === 'called'
                            && previousStatus !== 'called'
                            && !announcedWaitlistIdsRef.current.has(waitlistID)
                        );
                    })
                    .sort((left, right) => {
                        const leftTime = left.notifiedAt ? new Date(left.notifiedAt).getTime() : 0;
                        const rightTime = right.notifiedAt ? new Date(right.notifiedAt).getTime() : 0;
                        return leftTime - rightTime;
                    });

                previousStatusRef.current = nextStatusMap;

                if (!isActive || newlyCalledTokens.length === 0) {
                    return;
                }

                newlyCalledTokens.forEach((entry) => {
                    const waitlistID = String(entry.waitlistID);
                    announcedWaitlistIdsRef.current.add(waitlistID);
                    playWaitlistAnnouncement(buildWaitlistAnnouncementPayload(entry));
                });
            } catch (error) {
                // Ignore polling failures and retry on the next interval.
            } finally {
                isFetchingRef.current = false;
            }
        };

        fetchAndAnnounce();
        const intervalId = window.setInterval(fetchAndAnnounce, POLL_INTERVAL_MS);

        return () => {
            isActive = false;
            isFetchingRef.current = false;
            window.clearInterval(intervalId);
        };
    }, [isMonitoringRoute, location.pathname]);

    return null;
}

export default WaitlistAnnouncementMonitor;